import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { IsoDate, Range } from '@softline/core';
import { DateRange } from '@softline/ui-core';
import { DateRangeDropdownComponent } from '@softline/ui-core/date';
import { PageFilterItem } from '../../types/page-filter-item';
import { PageFilterService } from '../../services/page-filter.service';
import { Filter } from '../../../types/filter';
import { createDateRangeFilter } from '../../../functions/date-filter.function';
import { readDateRangeFilter } from '../../../helper/read-date-range-filter.function';

/** A pinned date range item: the design-system date range dropdown writes a between node. */
@Component({
  selector: 'soft-page-filter-date-range-item',
  imports: [DateRangeDropdownComponent],
  templateUrl: './page-filter-date-range-item.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageFilterDateRangeItemComponent {
  readonly item = input.required<PageFilterItem<any, any>>();
  readonly service = input.required<PageFilterService>();

  readonly open = signal(false);
  readonly title = computed(() => `${this.item().config.title()}`);
  readonly options = computed(() => this.item().date ?? {});
  readonly committed = computed(() => this.service().getSourceItemValue(this.item()));
  /** The committed range, or the source's initial period while nothing is committed - what the queries fall back to. */
  readonly committedRange = computed<DateRange | null>(() => {
    const item = this.item();
    const value =
      (this.committed() as { value?: Range<IsoDate> } | null)?.value ??
      readDateRangeFilter(item.source.state.config.initialValue, item.field)?.range;
    return value ? { from: value.from, to: value.to } : null;
  });

  apply(range: DateRange): void {
    const item = this.item();
    const value: Range<IsoDate> = { from: range.from as IsoDate, to: range.to as IsoDate };
    const current = this.committed() as (Filter<any> & { reference?: unknown }) | null;
    // a plain range item never carries a comparison: applying it here clears one, as the table page always did
    const node = current ? { ...current, type: 'date', operator: 'between', value } : createDateRangeFilter<any>(item.field, value);
    delete (node as { reference?: unknown }).reference;
    this.service().applyItemFilter(item, node as Filter<any>, 'source');
    this.close();
  }

  close(): void {
    this.open.set(false);
  }
}
